import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { API, getAuthHeader, STATUT_META, GUARD_STATUS_META, CCMI_COMPLIANCE_CHECKLIST, computeComplianceStatus } from './ccmiConstants';
import { useCcmiCompliance } from './useCcmiCompliance';
import { ComplianceStatusPanel, AutosaveIndicator } from './CcmiParts';

export function useCcmiDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ brouillon: 0, transmis_domium: 0, signe: 0, archive: 0, total: 0, actifs: 0 });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);  // V37.50.1 — erreur API visible
  const [filterStatut, setFilterStatut] = useState('all');
  const [search, setSearch] = useState('');

  // Modale creation
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    reference: '',
    maitre_ouvrage: { nom: '', prenom: '', email: '', telephone: '' },
    constructeur: { raison_sociale: '', siret: '' },
    terrain: { adresse: '' },
    prix_ttc: '',
    type_contrat: 'avec_plan',
    notes: '',
  });

  // Modale detail
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);  // V37.53
  const [importingPdf, setImportingPdf] = useState(false);    // V37.54-A
  const [analyzingDocId, setAnalyzingDocId] = useState(null); // V37.54-B
  const [reviewingDocId, setReviewingDocId] = useState(null); // V37.54-C
  const [reportingDocId, setReportingDocId] = useState(null); // V37.54-D
  const [compliancingDoc, setCompliancingDoc] = useState(null); // V37.55 — doc en cours de mise en conformite
  const [complianceDraft, setComplianceDraft] = useState(null); // payload formulaire V37.55
  const [complianceLoading, setComplianceLoading] = useState(false);
  // V38.17 — Sauvegarde explicite (sans génération PDF)
  const [complianceSaving, setComplianceSaving] = useState(false);
  // V38.18 — Autosave silencieux (debounce 2s)
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const [autosaveAt, setAutosaveAt] = useState(null); // timestamp ISO de la dernière save réussie
  const [autosaveError, setAutosaveError] = useState(null);
  const autosaveTimerRef = useRef(null);
  const autosaveBaselineRef = useRef(null); // snapshot du draft à l'ouverture (skip autosave initial)
  const [autosaveTick, setAutosaveTick] = useState(0); // re-render "il y a Xs"
  const [assigningJuriste, setAssigningJuriste] = useState(null); // V37.56 — doc en cours d'assignation
  const [juristeEmailInput, setJuristeEmailInput] = useState('');
  const [juristeCommentInput, setJuristeCommentInput] = useState('');
  const [juristeAssignLoading, setJuristeAssignLoading] = useState(false);
  // V37.71 — Paywall "Contrat définitif" (500 € HT)
  const [finalContractPaywall, setFinalContractPaywall] = useState(null); // {project, message, amount_ht}
  const [creatingFinalDevis, setCreatingFinalDevis] = useState(false);
  const [generatingFinalContract, setGeneratingFinalContract] = useState(false);
  const fileInputRef = React.useRef(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (filterStatut !== 'all') params.append('statut', filterStatut);
      else params.append('include_archived', 'true');
      if (search.trim()) params.append('reference', search.trim());
      params.append('limit', '100');
      const [listRes, countsRes] = await Promise.all([
        axios.get(`${API}/ccmi/projects?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/ccmi/projects/counts`, { headers: getAuthHeader() }),
      ]);
      setItems(listRes.data?.items || []);
      setCounts(countsRes.data?.counts || counts);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Erreur réseau';
      let msg;
      if (status === 401 || status === 403) {
        msg = `Session expirée ou accès refusé (HTTP ${status}). Merci de vous déconnecter puis de vous reconnecter.`;
      } else if (status === 429) {
        msg = 'Trop de requêtes (HTTP 429). Veuillez patienter quelques secondes puis cliquer sur « Rafraîchir ».';
      } else if (!err?.response) {
        msg = 'Impossible de joindre le serveur. Vérifiez votre connexion internet.';
      } else {
        msg = `Erreur ${status || ''} : ${detail}`;
      }
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filterStatut, search, counts]);

  // Init unique (pattern React 19)
  const fetchedOnce = React.useRef(null);
  if (fetchedOnce.current == null) {
    fetchedOnce.current = true;
    fetchList();
  }

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = { ...draft };
      payload.prix_ttc = draft.prix_ttc ? parseFloat(draft.prix_ttc) : null;
      const res = await axios.post(`${API}/ccmi/projects`, payload, { headers: getAuthHeader() });
      toast.success(`Projet CCMI ${res.data.project.reference} créé`);
      setShowCreate(false);
      setDraft({
        reference: '',
        maitre_ouvrage: { nom: '', prenom: '', email: '', telephone: '' },
        constructeur: { raison_sociale: '', siret: '' },
        terrain: { adresse: '' },
        prix_ttc: '',
        type_contrat: 'avec_plan',
        notes: '',
      });
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur création projet');
    } finally {
      setCreating(false);
    }
  };

  const handleSync = async (project) => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/domium/sync`,
        {},
        { headers: getAuthHeader() },
      );
      toast.success(`Dossier DOS DOMIUM ${res.data.domium_reference} ${res.data.sync.action === 'created' ? 'créé' : 'mis à jour'} (statut draft).`);
      setSelected(res.data.project);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur sync DOS DOMIUM');
    } finally {
      setSyncing(false);
    }
  };

  // V37.53 — Telecharger le PDF du projet de contrat CCMI
  const handleGeneratePdf = async (project) => {
    setGeneratingPdf(true);
    try {
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/contract-pdf`,
        {},
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `projet_contrat_ccmi_${project.reference || 'sans-ref'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const gedStored = res.headers?.['x-ged-stored'] === '1';
      const docId = res.headers?.['x-document-id'];
      toast.success(
        `Projet de contrat généré${gedStored ? ' et archivé en GED' : ''}${docId ? ` (${docId})` : ''}`,
      );
      // Re-fetch detail pour refleter le log dans l'historique
      try {
        const detail = await axios.get(`${API}/ccmi/projects/${project.id}`, { headers: getAuthHeader() });
        setSelected(detail.data?.project || project);
      } catch { /* non bloquant */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur génération PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // V37.71 — Génération contrat définitif avec paywall (500 € HT, service final_contract)
  const handleGenerateFinalContract = async (project) => {
    if (!project) return;
    if (!window.confirm(`Générer le contrat CCMI définitif pour ${project.reference} ?\n\nCe service est payant (500 € HT). Si le service n'est pas réglé, un devis vous sera proposé.\n\nLes clauses corrigées issues du projet de mise en conformité seront intégrées de manière professionnelle.`)) return;
    setGeneratingFinalContract(true);
    try {
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/generate-final-contract`,
        {},
        { headers: getAuthHeader() },
      );
      toast.success(`Contrat définitif ${res.data?.contract_number} généré (${res.data?.nb_corrections_integrees || 0} clauses intégrées)`);
      // Téléchargement immédiat
      try {
        const dl = await axios.get(
          `${API}/ccmi/projects/${project.id}/final-contract/${res.data.document_id}`,
          { headers: getAuthHeader(), responseType: 'blob' },
        );
        const blob = new Blob([dl.data], { type: 'application/pdf' });
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = `${res.data.contract_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(u);
      } catch { /* non bloquant */ }
      fetchList();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // V37.71 : 402 Payment Required → ouvrir paywall pour générer un devis
      if (status === 402 && detail && typeof detail === 'object' && detail.error === 'payment_required') {
        setFinalContractPaywall({
          project,
          message: detail.message,
          amount_ht: detail.amount_ht,
          ccmi_project_id: detail.ccmi_project_id,
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Erreur génération contrat définitif'));
      }
    } finally {
      setGeneratingFinalContract(false);
    }
  };

  // V37.71 — Création de devis pour le service final_contract (paywall)
  const handleCreateFinalContractDevis = async () => {
    if (!finalContractPaywall) return;
    const project = finalContractPaywall.project;
    const clientEmail = project?.maitre_ouvrage?.email
      || window.prompt('Email du client pour le devis du contrat définitif :', '');
    if (!clientEmail || !clientEmail.includes('@')) {
      toast.error('Email client requis pour générer le devis');
      return;
    }
    setCreatingFinalDevis(true);
    try {
      const res = await axios.post(
        `${API}/legalcoop/finance/devis`,
        {
          client_email: clientEmail.trim().toLowerCase(),
          client_name: [project?.maitre_ouvrage?.prenom, project?.maitre_ouvrage?.nom].filter(Boolean).join(' ') || null,
          ccmi_project_id: project.id,
          ccmi_project_reference: project.reference,
          service_type: 'final_contract',
          notes: `Devis pour la génération du Contrat CCMI définitif imprimable du dossier ${project.reference}.`,
        },
        { headers: getAuthHeader() },
      );
      toast.success(`Devis ${res.data?.devis?.quote_number} créé. Retrouvez-le dans LEGALCOOP > Finance.`);
      setFinalContractPaywall(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur création devis');
    } finally {
      setCreatingFinalDevis(false);
    }
  };

  // V37.54-A — Import d'un PDF de contrat existant
  const handleImportPdf = async (project, file) => {
    if (!file) return;
    if (!file.type || (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf'))) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('PDF trop volumineux (max 20 MB)');
      return;
    }
    setImportingPdf(true);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/contract-import`,
        form,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      const d = res.data || {};
      const ocrNote = d.requires_ocr ? ' — PDF scanné (OCR requis)' : '';
      const gedNote = d.ged?.stored ? ' • archivé GED' : '';
      toast.success(`PDF importé : ${d.document_id} (${d.pages_count}p, ${d.extraction_status})${ocrNote}${gedNote}`);
      // Re-fetch detail pour refleter le nouveau document
      try {
        const detail = await axios.get(`${API}/ccmi/projects/${project.id}`, { headers: getAuthHeader() });
        setSelected(detail.data?.project || project);
      } catch { /* non bloquant */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur import PDF';
      toast.error(detail);
    } finally {
      setImportingPdf(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // V37.54-B — Lancer l'analyse IA juridique sur un PDF importe
  const handleAnalyzeDoc = async (project, doc) => {
    if (doc.requires_ocr) {
      toast.error('OCR requise avant analyse (PDF scanné)');
      return;
    }
    setAnalyzingDocId(doc.document_id);
    try {
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/documents/${doc.document_id}/analyze`,
        {},
        { headers: getAuthHeader(), timeout: 120000 },
      );
      const a = res.data?.analysis || {};
      if (a.status === 'success') {
        const r = a.result || {};
        toast.success(
          `Analyse IA : score ${r.score_conformite}/100 — ${r.manquements?.length || 0} manquements`,
          { description: 'Aide à la revue (non avis juridique).' },
        );
      } else {
        toast.error(`Analyse impossible : ${a.error_message || a.reason || a.status}`);
      }
      // Re-fetch detail
      try {
        const detail = await axios.get(`${API}/ccmi/projects/${project.id}`, { headers: getAuthHeader() });
        setSelected(detail.data?.project || project);
      } catch { /* non bloquant */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur analyse IA');
    } finally {
      setAnalyzingDocId(null);
    }
  };

  // V37.54-C — Revue humaine d'une analyse IA
  const handleReviewAi = async (project, doc, decision) => {
    let comment = '';
    if (decision === 'pret_pour_validation_juridique') {
      comment = window.prompt(
        'Commentaire obligatoire (>=20 caractères) pour "Prêt pour validation juridique" :',
        '',
      ) || '';
      if (comment.trim().length < 20) {
        toast.error('Commentaire trop court (min 20 caractères requis)');
        return;
      }
    } else {
      comment = window.prompt('Commentaire (optionnel) :', '') || '';
    }
    setReviewingDocId(doc.document_id);
    try {
      await axios.post(
        `${API}/ccmi/projects/${project.id}/documents/${doc.document_id}/ai-review`,
        { decision, comment },
        { headers: getAuthHeader() },
      );
      toast.success(`Revue enregistrée : ${decision}`);
      try {
        const detail = await axios.get(`${API}/ccmi/projects/${project.id}`, { headers: getAuthHeader() });
        setSelected(detail.data?.project || project);
      } catch { /* non bloquant */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur revue IA');
    } finally {
      setReviewingDocId(null);
    }
  };

  // V37.54-D — Telecharger le rapport PDF de revue (analyse IA + decision humaine)
  const handleDownloadReviewReport = async (project, doc) => {
    setReportingDocId(doc.document_id);
    try {
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/documents/${doc.document_id}/review-report-pdf`,
        {},
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_revue_ccmi_${project.reference || 'sans-ref'}_${doc.document_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const gedStored = res.headers?.['x-ged-stored'] === '1';
      const reportId = res.headers?.['x-document-id'];
      toast.success(
        `Rapport de revue généré${gedStored ? ' et archivé en GED' : ''}${reportId ? ` (${reportId})` : ''}`,
      );
      try {
        const detail = await axios.get(`${API}/ccmi/projects/${project.id}`, { headers: getAuthHeader() });
        setSelected(detail.data?.project || project);
      } catch { /* non bloquant */ }
    } catch (err) {
      let detail = err?.response?.data?.detail;
      if (!detail && err?.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          detail = JSON.parse(txt)?.detail;
        } catch { /* ignore */ }
      }
      toast.error(detail || 'Erreur génération rapport');
    } finally {
      setReportingDocId(null);
    }
  };

  // V37.55 — Ouvrir le modal de mise en conformite (preremplissage)

  const _compliance = useCcmiCompliance({ assigningJuriste, autosaveAt, autosaveBaselineRef, autosaveTimerRef, complianceDraft, complianceLoading, complianceSaving, compliancingDoc, draft, juristeCommentInput, juristeEmailInput, selected, setAssigningJuriste, setAutosaveAt, setAutosaveError, setAutosaveStatus, setAutosaveTick, setComplianceDraft, setComplianceLoading, setComplianceSaving, setCompliancingDoc, setJuristeAssignLoading, setJuristeCommentInput, setJuristeEmailInput, setSelected });
  const { openComplianceModal, submitComplianceDraft, saveComplianceDraft, triggerAutosave, openJuristeAssign, submitJuristeAssign } = _compliance;

  const handleArchive = async (project) => {
    if (!window.confirm(`Archiver le projet ${project.reference} ? (réversible)`)) return;
    setArchiving(true);
    try {
      const res = await axios.patch(
        `${API}/ccmi/projects/${project.id}`,
        { statut: 'archive' },
        { headers: getAuthHeader() },
      );
      toast.success('Projet archivé');
      setSelected(res.data.project);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur archivage');
    } finally {
      setArchiving(false);
    }
  };

  // ctx-bag : état + handlers partagés avec les dialogs extraits

  const ctx = {
    analyzingDocId, archiving, assigningJuriste, autosaveAt, autosaveError, autosaveStatus,
    autosaveTick, complianceDraft, complianceLoading, complianceSaving, compliancingDoc, counts,
    creating, creatingFinalDevis, draft, filterStatut, finalContractPaywall,
    generatingFinalContract, generatingPdf, importingPdf, items, juristeAssignLoading,
    juristeCommentInput, juristeEmailInput, loadError, loading, reportingDocId, reviewingDocId,
    search, selected, showCreate, syncing,
    setAnalyzingDocId, setArchiving, setAssigningJuriste, setAutosaveAt, setAutosaveError,
    setAutosaveStatus, setAutosaveTick, setComplianceDraft, setComplianceLoading, setComplianceSaving,
    setCompliancingDoc, setCounts, setCreating, setCreatingFinalDevis, setDraft,
    setFilterStatut, setFinalContractPaywall, setGeneratingFinalContract, setGeneratingPdf,
    setImportingPdf, setItems, setJuristeAssignLoading, setJuristeCommentInput, setJuristeEmailInput,
    setLoadError, setLoading, setReportingDocId, setReviewingDocId, setSearch, setSelected,
    setShowCreate, setSyncing,
    autosaveBaselineRef, autosaveTimerRef, fetchList, fetchedOnce, fileInputRef, handleAnalyzeDoc,
    handleArchive, handleCreate, handleCreateFinalContractDevis, handleDownloadReviewReport,
    handleGenerateFinalContract, handleGeneratePdf, handleImportPdf, handleReviewAi, handleSync,
    navigate, openComplianceModal, openJuristeAssign, saveComplianceDraft, submitComplianceDraft,
    submitJuristeAssign, triggerAutosave,
    STATUT_META, GUARD_STATUS_META, getAuthHeader, CCMI_COMPLIANCE_CHECKLIST, computeComplianceStatus,
    ComplianceStatusPanel, AutosaveIndicator,
  };

  return ctx;
}
